import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, Package, ArrowRight, Copy, Mail, Truck, Clock, QrCode, Banknote } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getCheckoutStatus, getOrderQrPayment } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { toast } from "sonner";

export default function OrderConfirmPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const orderId = searchParams.get("order_id");
    const orderNumber = searchParams.get("order_number");
    const paymentMethodParam = searchParams.get("payment_method");
    const { clearCart } = useCart();
    const { customer } = useCustomerAuth();
    const [status, setStatus] = useState(sessionId ? "loading" : "success");
    const [attempts, setAttempts] = useState(0);
    const [qrData, setQrData] = useState(null);
    const [orderData, setOrderData] = useState(null);

    const clearCartRef = useRef(clearCart);
    clearCartRef.current = clearCart;

    useEffect(() => {
        if (!sessionId) {
            clearCartRef.current();
            return;
        }
        const poll = async () => {
            if (attempts >= 6) { setStatus("success"); clearCartRef.current(); return; }
            try {
                const res = await getCheckoutStatus(sessionId);
                const data = res.data;
                if (data.payment_status === "paid" || data.status === "complete") {
                    setStatus("success"); clearCartRef.current();
                } else if (data.status === "expired") {
                    setStatus("expired");
                } else {
                    setTimeout(() => setAttempts(a => a + 1), 2000);
                }
            } catch { setTimeout(() => setAttempts(a => a + 1), 2000); }
        };
        if (status === "loading") poll();
    }, [attempts, sessionId, status]);

    // Fetch QR code for bank transfer
    useEffect(() => {
        if (orderId && paymentMethodParam === "transfer") {
            getOrderQrPayment(orderId).then(r => setQrData(r.data)).catch(() => {});
        }
    }, [orderId, paymentMethodParam]);

    // Fetch order data for summary
    useEffect(() => {
        if (orderId) {
            import("../lib/api").then(({ default: api }) => {
                api.get(`/orders/public/${orderId}`).then(r => setOrderData(r.data)).catch(() => {});
            });
        }
    }, [orderId]);

    const copyOrderNumber = () => {
        if (orderNumber) {
            navigator.clipboard.writeText(orderNumber);
            toast.success("Číslo objednávky zkopírováno");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50/50">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-16">
                {status === "loading" ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
                        <Loader2 size={40} className="animate-spin text-green-600 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Ověřuji platbu...</h2>
                        <p className="text-gray-500 text-sm">Prosím vyčkejte chvíli</p>
                    </div>
                ) : status === "expired" ? (
                    <div className="bg-white rounded-2xl border border-red-200 p-12 text-center shadow-sm">
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
                            <Package size={30} className="text-red-400" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Platba vypršela</h2>
                        <p className="text-gray-500 mb-6">Platební relace vypršela. Zkuste to prosím znovu.</p>
                        <Link to="/kosik" className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white rounded-full px-8 h-10 text-sm font-semibold transition-colors">
                            Zpět do košíku
                        </Link>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center shadow-sm" data-testid="order-confirm-success">
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle size={42} className="text-green-500" />
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-testid="order-confirm-title">
                            Objednávka přijata!
                        </h1>
                        <p className="text-gray-500 mb-6">
                            Děkujeme za váš nákup. Potvrzení bylo odesláno na váš email.
                        </p>

                        {/* Order number */}
                        {orderNumber && (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6 inline-block" data-testid="order-number-box">
                                <p className="text-xs text-green-600 font-semibold mb-1 uppercase tracking-wider">Číslo objednávky</p>
                                <div className="flex items-center gap-3 justify-center">
                                    <span className="text-2xl font-mono font-bold text-green-800" data-testid="order-number-value">
                                        {orderNumber}
                                    </span>
                                    <button onClick={copyOrderNumber}
                                        className="p-1.5 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                                        title="Kopírovat" data-testid="copy-order-number">
                                        <Copy size={16} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Order price summary */}
                        {orderData && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6 text-left max-w-sm mx-auto" data-testid="order-summary-box">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Shrnutí objednávky</p>
                                <div className="space-y-1.5 text-sm">
                                    <div className="flex justify-between text-gray-600">
                                        <span>Mezisoučet ({orderData.items_count} položek)</span>
                                        <span>{(orderData.subtotal || 0).toLocaleString("cs-CZ")} Kč</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span>Doprava</span>
                                        <span>{orderData.shipping_cost === 0 ? "Zdarma" : `${(orderData.shipping_cost || 0).toLocaleString("cs-CZ")} Kč`}</span>
                                    </div>
                                    {orderData.cod_fee > 0 && (
                                        <div className="flex justify-between text-gray-600">
                                            <span>Dobírka</span>
                                            <span>{orderData.cod_fee} Kč</span>
                                        </div>
                                    )}
                                    {orderData.coupon_discount > 0 && (
                                        <div className="flex justify-between text-green-600 font-semibold" data-testid="confirm-coupon-discount">
                                            <span>Sleva (kupón {orderData.coupon_code})</span>
                                            <span>-{(orderData.coupon_discount || 0).toLocaleString("cs-CZ")} Kč</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-200 pt-2 mt-2">
                                        <span>Celkem</span>
                                        <span data-testid="confirm-total">{(orderData.total || 0).toLocaleString("cs-CZ")} Kč</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* QR Code for Bank Transfer */}
                        {qrData && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 text-left" data-testid="qr-payment-section">
                                <div className="flex items-center gap-2 mb-4">
                                    <QrCode size={18} className="text-blue-600" />
                                    <h3 className="font-semibold text-blue-900">Platba bankovnim prevodem</h3>
                                </div>
                                <p className="text-sm text-blue-700 mb-4">
                                    Naskenujte QR kod v mobilni aplikaci vasi banky pro rychlou platbu:
                                </p>
                                <div className="flex flex-col sm:flex-row gap-6 items-center">
                                    <div className="bg-white p-3 rounded-xl border border-blue-200 shadow-sm">
                                        <img src={qrData.qr_image} alt="QR platba" className="w-40 h-40" data-testid="qr-code-image" />
                                    </div>
                                    <div className="flex-1 space-y-2.5 text-sm w-full">
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-blue-600 font-medium">Cislo uctu:</span>
                                            <span className="font-mono font-bold text-gray-900">{qrData.bank_account}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-blue-600 font-medium">IBAN:</span>
                                            <span className="font-mono text-xs text-gray-800">{qrData.iban}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-blue-600 font-medium">Castka:</span>
                                            <span className="font-bold text-gray-900">{(qrData.amount || 0).toLocaleString("cs-CZ")} Kc</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-blue-600 font-medium">Variabilni symbol:</span>
                                            <span className="font-mono font-bold text-gray-900">{qrData.variable_symbol}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5">
                                            <span className="text-blue-600 font-medium">Zprava:</span>
                                            <span className="text-gray-800">{qrData.message}</span>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs text-blue-500 mt-4 text-center">
                                    Zbozi bude expedovano po prijeti platby na nas ucet (obvykle 1-2 pracovni dny).
                                </p>
                            </div>
                        )}

                        {/* Info cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-left">
                            <div className="bg-gray-50 rounded-xl p-4">
                                <Mail size={16} className="text-green-600 mb-2" />
                                <p className="text-xs font-semibold text-gray-700">Email potvrzení</p>
                                <p className="text-[11px] text-gray-500">Obdržíte shrnutí objednávky na email</p>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <Truck size={16} className="text-green-600 mb-2" />
                                <p className="text-xs font-semibold text-gray-700">Expedice</p>
                                <p className="text-[11px] text-gray-500">Zásilka bude odeslána do 48 hodin</p>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <Clock size={16} className="text-green-600 mb-2" />
                                <p className="text-xs font-semibold text-gray-700">Sledování</p>
                                <p className="text-[11px] text-gray-500">Tracking číslo obdržíte emailem</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <Link to="/kategorie" className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-full h-11 text-sm font-semibold transition-colors px-6" data-testid="confirm-continue-btn">
                                Pokračovat v nákupu <ArrowRight size={14} />
                            </Link>
                            <Link to={customer ? "/muj-ucet" : "/prihlaseni"} className="flex items-center justify-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-full h-10 text-sm font-medium text-gray-700 transition-colors px-6" data-testid="confirm-account-btn">
                                {customer ? "Můj účet / Sledovat objednávku" : "Přihlásit se / Sledovat objednávku"}
                            </Link>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
